import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Grid, Button, TextField,
  FormHelperText,
} from '@material-ui/core';
import * as yup from 'yup';
import AutocompleteSpecialty from 'shared/components/AutocompleteSpecialty';
import LocationsPopper from 'shared/components/BlockButtonsSearch/partials/LocationsPopper';
import RangeDatePicker from 'shared/components/RangeDatePicker';
import useQueryString from 'shared/hooks/useQueryString';
import { useBackendUserState } from 'contexts/UserContext';

const phoneValidator = value => {
  if (/([)(]{2,}|[\s+-]{2,})/.test(value)) return false;
  if (!/^(|[0-9)(\s+-]{10,20})$/.test(value)) return false;
  return true;
};

const schema = yup.object().shape({
  firstName: yup.string().required('Field is required')
    .test(
      'not-only-spaces',
      'First Name cannot contain only spaces',
      value => value && value.trim().length > 0 && value.length > 0,
    )
    .test(
      'contains-space',
      'Please use  “-“ for hyphenated name',
      value => value && !/\s/.test(value.trim()),
    ),
  lastName: yup.string().required('Field is required')
    .test(
      'not-only-spaces',
      'Last Name cannot contain only spaces',
      value => value && value.trim().length > 0 && value.length > 0,
    )
    .test(
      'contains-space',
      'Please use  “-“ for hyphenated name',
      value => value && !/\s/.test(value.trim()),
    ),
  phoneNumber: yup.string().required('Field is required')
    .test(
      'not-only-spaces',
      'Phone Number cannot contain only spaces',
      value => value && value.trim().length > 0 && value.length > 0,
    )
    .test(
      'phonetest2',
      'Enter a valid phone number',
      value => value && phoneValidator(value),
    ),
  email: yup.string().email('Email must be a valid email').matches(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9.-]+$/, 'Email must be a valid email').required('Field is required'),
  specialty: yup.array()
    .min(1, 'At least one item should be selected')
    .required('Field is required'),
  dateRange: yup.array()
    .min(2, 'Start Date and End Date should be selected')
    .test(
      'not-null-waitlist',
      'Field is required 2',
      val => val?.length > 0 && val?.every(v => v !== null),
    )
    .test(
      'valid-date-format-waitlist',
      'The date should be in mm/dd/yy format',
      val => val?.every(v => v === null || (v instanceof Date && !Number.isNaN(Date.parse(v)))),
    )
    .test(
      'valid-date-range-waitlist',
      'Start Date should be before End Date',
      val => {
        if (val?.every(v => v !== null && v instanceof Date && !Number.isNaN(Date.parse(v)))) {
          return val[0] <= val[1];
        }
        return true;
      },
    )
    .test(
      'in-future-waitlist',
      'This date should be in the future',
      val => {
        if (val?.every(v => v !== null && v instanceof Date && !Number.isNaN(Date.parse(v)))) {
          return val?.every(v => v >= new Date(new Date().setHours(0, 0, 0, 0)));
        }
        return true;
      },
    )
    .required('Field is required 1'),
  hours: yup.number()
    .typeError('This field must be a number')
    .positive('This number must be a positive')
    .integer('This number must be an integer')
    .max(1000, 'This number must be at most 1000')
    .required('This field is required'),
  location: yup.array()
    .min(1, 'At least one item should be selected')
    .required('Field is required'),
});

const schemaUser = yup.object().shape({
  specialty: yup.array()
    .min(1, 'At least one item should be selected')
    .required('Field is required'),
  dateRange: yup.array()
    .min(2, 'Start Date and End Date should be selected')
    .test(
      'not-null-waitlist',
      'Field is required 2',
      val => val?.length > 0 && val?.every(v => v !== null),
    )
    .test(
      'valid-date-format-waitlist',
      'The date should be in mm/dd/yy format',
      val => val?.every(v => v === null || (v instanceof Date && !Number.isNaN(Date.parse(v)))),
    )
    .test(
      'valid-date-range-waitlist',
      'Start Date should be before End Date',
      val => {
        if (val?.every(v => v !== null && v instanceof Date && !Number.isNaN(Date.parse(v)))) {
          return val[0] <= val[1];
        }
        return true;
      },
    )
    .test(
      'in-future-waitlist',
      'This date should be in the future',
      val => {
        if (val?.every(v => v !== null && v instanceof Date && !Number.isNaN(Date.parse(v)))) {
          return val?.every(v => v >= new Date(new Date().setHours(0, 0, 0, 0)));
        }
        return true;
      },
    )
    .required('Field is required 1'),
  hours: yup.number()
    .typeError('This field must be a number')
    .positive('This number must be a positive')
    .integer('This number must be an integer')
    .max(1000, 'This number must be at most 1000')
    .required('This field is required'),
  location: yup.array()
    .min(1, 'At least one item should be selected')
    .required('Field is required'),
});

const WhitelistForm = ({ onSubmit, buttonText }) => {
  // const dispatch = useDispatch();
  const { state: { user: backendUser } } = useBackendUserState();
  const [open, setOpen] = useState(false);
  const { urlParams } = useQueryString();
  const specialty = urlParams.specialty || [];
  const specialtyDefault = Array.isArray(specialty) ? specialty : [specialty];
  const credentials = urlParams.credentials || [];
  const credentialsDefault = Array.isArray(credentials) ? credentials : [credentials];
  const costDefault = (urlParams.costFrom || urlParams.costTo) ? [
    parseInt(urlParams.costMin, 10), parseInt(urlParams.costMax, 10),
  ] : [];

  const getDateRange = dateRange => {
    let start = null;
    let end = null;
    if (dateRange) {
      if (dateRange.length > 1) {
        start = new Date(dateRange[0]);
        end = new Date(dateRange[1]);
      }
    }
    return [start, end];
  };

  const [queryStringValue, setQueryStringValue] = useState({
    preceptorUid: urlParams.preceptorUid || '',
    dateRange: getDateRange(urlParams.dateRange ? urlParams.dateRange : [urlParams.dateFrom, urlParams.dateTo]),
    dateFrom: urlParams.dateRange ? urlParams.dateRange[0] : urlParams.dateFrom,
    dateTo: urlParams.dateRange ? urlParams.dateRange[1] : urlParams.dateTo,
    hours: urlParams.hours || '',
    specialty: specialtyDefault,
    cost: costDefault,
    credentials: credentialsDefault,
    o: urlParams.o || '-availability',
    compactStates: urlParams.compactStates || [],
    location: urlParams.location || [],
  });
  const {
    control, handleSubmit, setValue, register, setError, formState: { errors },
  } = useForm({
    resolver: yupResolver(backendUser ? schemaUser : schema),
    reValidateMode: 'onBlur',
    defaultValues: {
      hours: queryStringValue.hours ? parseInt(queryStringValue.hours, 10) : null,
      dateRange: queryStringValue.dateRange,
      specialty: specialtyDefault,
      location: urlParams.location || [],
    },
  });

  useEffect(() => {
    setValue('location', queryStringValue.location);
  }, [setValue, queryStringValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container spacing={1}>
        {!backendUser && (
          <>
            <Grid item xs={12}>
              <TextField
                autoComplete="fname"
                name="firstName"
                variant="filled"
                fullWidth
                id="firstName"
                label="First Name"
                autoFocus
                {...register('firstName')}
                error={Boolean(errors.firstName?.message)}
                helperText={errors.firstName?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                autoComplete="lname"
                {...register('lastName')}
                error={Boolean(errors.lastName?.message)}
                helperText={errors.lastName?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                {...register('email')}
                error={Boolean(errors.email?.message)}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                id="phone"
                label="Phone Number"
                name="phoneNumber"
                autoComplete="phoneNumber"
                {...register('phoneNumber')}
                error={Boolean(errors.phoneNumber?.message)}
                helperText={errors.phoneNumber?.message}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Controller
            name="specialty"
            control={control}
            render={({ field }) => (
              <AutocompleteSpecialty
                {...field}
                label="Specialty"
                length={15}
                error={Boolean(errors.specialty?.message)}
                helperText={errors.specialty?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="location"
            control={control}
            render={() => (
              <LocationsPopper
                setQueryStringValue={setQueryStringValue}
                buttonAsAutocomplete
                setPreventSearch={() => {}}
                noSubmit
                error={Boolean(errors.location?.message)}
                setError={setError}
              />
            )}
          />
          {errors.location?.message && (
            <FormHelperText error style={{ margin: '0px 14px' }}>
              {errors.location?.message}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="dateRange"
            control={control}
            render={({ field: { onChange, value } }) => (
              <RangeDatePicker
                minDate={new Date()}
                open={open}
                setOpen={setOpen}
                value={value}
                onChange={ev => {
                  onChange(ev);
                  setError('dateRange', null);
                }}
                error={errors.dateRange}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="hours"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                variant="filled"
                fullWidth
                id="hours"
                label="Required Hours"
                name="hours"
                autoComplete="hours"
                onChange={ev => {
                  onChange(ev);
                  setError('hours', null);
                }}
                value={value}
                error={Boolean(errors.hours?.message)}
                helperText={errors.hours?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Button type="submit" fullWidth variant="contained" mt={2}>
            {buttonText}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

WhitelistForm.defaultProps = {
  buttonText: 'Join Waitlist',
};

WhitelistForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  buttonText: PropTypes.string,
};

export default WhitelistForm;
